import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, TableHead, TableRow, TableCell, TableBody, Button, CircularProgress, Box, Typography, TableContainer, Paper, TablePagination,
  TextField
} from '@mui/material';

const PendingPaymentsList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [txnInputs, setTxnInputs] = useState({}); // map item._id -> input value
  const [txnLoading, setTxnLoading] = useState({}); // map item._id -> boolean
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchData = async (p = page, l = rowsPerPage) => {
    setLoading(true);
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URI}/washes/payment/pending?page=${p + 1}&limit=${l}`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      const data = await res.json();
      setItems(Array.isArray(data.data) ? data.data : []);
      setTotal(data.total || (Array.isArray(data.data) ? data.data.length : 0));
    } catch (err) {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [page, rowsPerPage]);

  const handleMarkPaid = async (item) => {
    if (!window.confirm(`Mark payment of ${item.amount || 'N/A'} for ${item.userName || item.name || 'user'} as paid?`)) return;
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    try {
      // call backend to mark paid (adjust endpoint if different)
      const res = await fetch(`${process.env.REACT_APP_SERVER_URI}/payments/mark-paid/${item._id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
        body: JSON.stringify({ markedBy: 'crm' }),
      });
      await res.json();
      // refresh current page to keep pagination consistent
      fetchData(page, rowsPerPage);
    } catch (err) {
      alert('Failed to mark paid.');
    }
  };

  const handleTxnChange = (rowId, value) => {
    setTxnInputs(prev => ({ ...prev, [rowId]: value }));
  };

  const handleSubmitTxn = async (item) => {
    const value = (txnInputs[item._id] || item.transactionId || '').trim();
    if (!value) {
      alert('Please enter a transaction id.');
      return;
    }
    // try to derive subscription id from item (fallback to item._id)
    const subId = item.subscriptionId || item.subscription || item.subId || item._id;
    if (!subId) {
      alert('Subscription id not found for this item.');
      return;
    }
    if (!window.confirm(`Set transaction id "${value}" for subscription ${subId}?`)) return;
    setTxnLoading(prev => ({ ...prev, [item._id]: true }));
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URI}/subscriptions/update/${subId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ transactionId: value }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to update subscription');
      // update local row
      setItems(prev => prev.map(it => it._id === item._id ? { ...it, transactionId: value, status: 'paid' } : it));
      alert('Transaction ID saved.');
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to save transaction id.');
    } finally {
      fetchData();
      setTxnLoading(prev => ({ ...prev, [item._id]: false }));
    }
  };

  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>;

  return (
    <Box>
      {items.length === 0 ? (
        <Typography>No pending payments found.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Car</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Transaction ID</TableCell>
                {/* <TableCell align="right">Action</TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(it => (
                <TableRow key={it._id}>
                  <TableCell>{it.subscriptionExpireDate ? new Date(it.subscriptionExpireDate).toLocaleString() : '-'}</TableCell>
                  <TableCell>
                    {(() => {
                      const display = it.userName || it.name || (it.user && it.user.name) || '-';
                      const userId = it.userId || it.user?._id || it.user?._id;
                      if (userId) {
                        return (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => navigate(`/users/${userId}`)}
                            sx={{ textTransform: 'none', p: 0 }}
                          >
                            {display}
                          </Button>
                        );
                      }
                      return display;
                    })()}
                  </TableCell>
                  <TableCell>{it.carRegNo || (it.car && it.car.regNo) || '-'}</TableCell>
                  <TableCell align="right">{it.price != null ? it.price : '-'}</TableCell>
                  <TableCell>{it.status || 'pending'}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                      <TextField
                        size="small"
                        value={txnInputs[it._id] ?? (it.transactionId ?? '')}
                        onChange={(e) => handleTxnChange(it._id, e.target.value)}
                        placeholder="Transaction ID"
                        sx={{ minWidth: 180 }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSubmitTxn(it)}
                        disabled={!!txnLoading[it._id]}
                      >
                        {txnLoading[it._id] ? <CircularProgress size={18} /> : 'Submit'}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5,10,25,50]}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default PendingPaymentsList;
